/**
 * Nosto add-to-cart attribution.
 *
 * Records an add-to-cart against the Nosto surface it came from — a recommendation
 * placement (`cp` attribution) or a search surface (SERP / category / autocomplete).
 * Without this, a user who adds straight from a Nosto card never generates an ATC
 * event, so any resulting purchase isn't credited back to the campaign.
 *
 * The hook is a single delegated listener on the theme's own `variant:add`
 * (assets/theme.js), dispatched on the submitted form with `bubbles: true` and only
 * after `response.ok`. Firing on success rather than on click means abandoned modals
 * can't over-count, and it sidesteps the markup differences between the two add paths.
 *
 * The split of responsibilities matters:
 *
 *   - Product identity comes from the EVENT PAYLOAD (`detail.items[0]`) — the Shopify
 *     add.js response forwarded verbatim, so it's ground truth for what was actually
 *     added, and identical for both add paths. Note `product_id` is the product and
 *     `id` is the variant/SKU; the card's `[name="id"]` input holds the *variant* id,
 *     which is why reading identity out of the form would attribute to the wrong thing.
 *   - The surface comes from the DOM. Because the event bubbles, `e.target` is the
 *     specific form that was submitted, so walking up from it can only ever reach that
 *     card's own placement. This is what keeps the same product in two widgets on one
 *     page attributed separately — nothing in the payload could tell those apart.
 *
 * Both the theme's native grid and Nosto's grid render the same <product-card> markup
 * and coexist in the DOM (the anti-flash fallback on collection pages), so resolving
 * the surface — and returning null for cards that aren't on a Nosto surface — is what
 * stops ordinary collection adds being logged as Nosto ones.
 *
 * DEPENDENCY: theme.js redirects to /cart *before* dispatching `variant:add` when
 * `themeVariables.settings.cartType === "page"`. The live setting is "drawer"; flipping
 * it to "page" silently zeroes all attribution here.
 */

import { anyEnabled } from './_nosto-flags.js';

// Nosto's own Portal targets — these mirror orlakiely-nosto/src/config.ts (`selectors`).
// If the search-templates app changes its mount points, these must change with it.
const AUTOCOMPLETE = '#nosto-autocomplete-dropdown';
const CATEGORY = '#nosto-category';
const SERP = '.main-search';

// Backstop only. `pending` is really cleared by the dialog:after-hide listener below;
// this bound just stops a stale entry living forever if that clear ever breaks. Don't
// tune it — a pending surviving five minutes means the clear is broken, and shortening
// this would only mask that.
const PENDING_TTL = 5 * 60 * 1000;

/**
 * Debug logging. Off unless switched on; costs nothing in production.
 *
 * Enable (persists across page loads — a window flag alone would be wiped by every
 * navigation, which is useless for click-through testing):
 *
 *   nostoAttr.on()      then click around; nostoAttr.off() when done
 *   nostoAttr.events    every record this page made, newest last — check the COUNT here
 *                       rather than trusting the console not to have swallowed a line
 *   nostoAttr.clear()   reset that array between test cases
 *
 * ?nostoattrdebug=true in the URL turns it on for the session too (handy on a device
 * where you can't reach a console to type).
 *
 * NOTE what this can and cannot tell you: it proves this file reached a given branch and
 * what it passed to the Nosto API. It does NOT prove Nosto's servers received or ingested
 * anything — for that, watch the network tab or Nosto's own debug toolbar (?nostodebug=true).
 */
const DEBUG_KEY = 'nostoAttributionDebug';

function debugEnabled() {
  if (window.nostoAttributionDebug) return true;
  try {
    return window.localStorage.getItem(DEBUG_KEY) === 'true';
  } catch (e) {
    return false; // storage can throw in private mode / blocked-cookie contexts
  }
}

function setDebug(on) {
  try {
    if (on) window.localStorage.setItem(DEBUG_KEY, 'true');
    else window.localStorage.removeItem(DEBUG_KEY);
  } catch (e) {
    window.nostoAttributionDebug = on; // fall back to the (page-scoped) window flag
  }
  console.log('[nosto-attr] debug ' + (on ? 'ON — click an add-to-cart' : 'off'));
}

function debug(msg, data) {
  if (!debugEnabled()) return;
  if (data === undefined) console.log('[nosto-attr] ' + msg);
  else console.log('[nosto-attr] ' + msg, data);
}

// Only exposed while Nosto is on — `record()` reads window.nostoAttr.events, and that
// only ever runs after init(), which is gated on the same flag at the bottom of the file.
if (anyEnabled()) {
  window.nostoAttr = {
    on: () => setDebug(true),
    off: () => setDebug(false),
    events: [],
    clear() {
      this.events.length = 0;
      console.log('[nosto-attr] events cleared');
    }
  };
}

/**
 * Which Nosto surface, if any, does this theme <product-card> sit in?
 *
 * "Surface" is our word, not Nosto's — they have no term spanning both of their APIs. It
 * means whichever Nosto-rendered context a card is sitting in: a recommendation placement,
 * or one of the three search contexts. It's a region of the DOM, not a page — one page can
 * hold several (two carousels at once; Nosto's category grid beside the theme's native
 * one), which is why it's resolved by walking up from the card rather than from the URL.
 * Recos resolve to a specific instance (`ref` = which placement); search resolves only to
 * a type, because that is all each API accepts.
 *
 * Returns null for a card on no Nosto surface (the native collection grid), and for
 * no card at all (the PDP main form, the cart page) — both must record nothing.
 */
function resolveSurface(card) {
  if (!card) {
    debug('surface: none — no <product-card> ancestor (PDP main form / cart page) → will not record');
    return null;
  }

  const handle = card.getAttribute('data-la-dn-product-handle');

  const placement = card.closest('.nosto_element');
  if (placement) {
    debug('surface: reco placement "' + placement.id + '" (card: ' + handle + ')');
    return { kind: 'reco', ref: placement.id };
  }
  if (card.closest(AUTOCOMPLETE)) {
    debug('surface: search/autocomplete (card: ' + handle + ')');
    return { kind: 'search', type: 'autocomplete' };
  }
  if (card.closest(CATEGORY)) {
    debug('surface: search/category (card: ' + handle + ')');
    return { kind: 'search', type: 'category' };
  }
  if (card.closest(SERP)) {
    debug('surface: search/serp (card: ' + handle + ')');
    return { kind: 'search', type: 'serp' };
  }

  debug('surface: none — card "' + handle + '" is on no Nosto surface (native grid?) → will not record');
  return null;
}

function record(surface, productId, skuId) {
  if (!surface) return; // resolveSurface already logged why
  if (!productId) {
    debug('NOT recording — no product_id in the variant:add payload');
    return;
  }

  // Nosto's script is injected by the Shopify app and has no theme-side stub, so a
  // consent tool (Pandectes) or an ad blocker can keep it out entirely. Bail rather
  // than throw a ReferenceError on every add.
  if (typeof window.nostojs !== 'function') {
    debug('NOT recording — window.nostojs is absent (blocked by consent tool or ad blocker?)');
    return;
  }

  // Payload ids are JSON numbers; recordAttribution.target and SearchHit.productId
  // are both typed string.
  const target = String(productId);
  const sku = skuId ? String(skuId) : undefined;

  const entry = surface.kind === 'reco'
    ? { call: 'recordAttribution', type: 'cp', target, ref: surface.ref }
    : { call: 'recordSearchAddToCart', searchType: surface.type, productId: target, skuId: sku };
  if (debugEnabled()) {
    window.nostoAttr.events.push(entry);
    debug('RECORD #' + window.nostoAttr.events.length + ' →', entry);
  }

  window.nostojs((api) => {
    if (surface.kind === 'reco') {
      // Nosto advise omitting `ref` because tracking resolves the placement from the
      // nearest .nosto_element ancestor — but that only holds for tracked link clicks,
      // where Nosto has the DOM event. recordAttribution takes a plain object with no
      // element context, so there's nothing for it to walk from. We already know the
      // placement id, so pass it explicitly.
      api.recordAttribution({ type: 'cp', target, ref: surface.ref }).done();
    } else {
      api.recordSearchAddToCart(surface.type, { productId: target, skuId: sku });
    }
  });
}

/**
 * Surface resolved at click time for the quick-buy modal path.
 *
 * Multi-variant cards (strictly: not `variants.size == 1 && selling_plan_groups.size == 0`)
 * open <quick-buy-modal>, which theme.js reparents to <body>. That severs the bubble path,
 * so `variant:add` from inside the modal never passes through the card or the placement —
 * the surface has to be captured before the modal opens.
 *
 * Storing the resolved surface rather than the card node keeps no DOM reference and can't
 * be invalidated by a re-render. That's defensive rather than load-bearing today (the
 * cards do stay attached), but it means a future Preact change can't quietly break this.
 */
let pending = null;

let bound = false;
function init() {
  if (bound) return;
  bound = true;

  // Resolve the surface while the card is definitely still in place. Single-variant
  // clicks set this too and it's simply unused — the card-ancestor path wins below.
  // No capture needed: nothing in the ATC path calls stopPropagation.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.product-card__quick-add-button');
    if (!btn) return;
    debug('quick-add clicked — resolving surface now, before any modal can reparent');
    pending = { surface: resolveSurface(btn.closest('product-card')), t: Date.now() };
  });

  // CAPTURE PHASE, deliberately: theme.js dispatches dialog:after-hide with no
  // `bubbles`, so a document-level bubble-phase listener would never see it and
  // `pending` would never clear.
  //
  // The quick-buy-modal check is load-bearing, not a tidy-up. dialog:after-hide is
  // inherited from DialogElement, which thirteen classes extend — HeaderSearch,
  // CartDrawer, HeaderSidebar, FacetsDrawer, the popovers. Unscoped, any one of them
  // closing wipes `pending`, and that silently breaks the autocomplete path: the modal
  // is reparented to <body>, so <header-search> no longer contains it, its outside-click
  // check (`!this.contains(target)`) fires on the add click and it starts hiding. Its
  // 0.2-0.3s leave animation then beats the add's two network round-trips
  // (POST /cart/add.js → GET /cart.js), so `pending` is gone before variant:add lands.
  // The failure is intermittent and network-dependent — the kind that looks like flaky
  // reporting rather than a bug. Don't drop the guard.
  document.addEventListener(
    'dialog:after-hide',
    (e) => {
      if (!(e.target instanceof Element) || !e.target.matches('quick-buy-modal')) return;
      if (pending) debug('dialog:after-hide (quick-buy-modal) → pending cleared');
      pending = null;
    },
    true
  );

  document.addEventListener('variant:add', (e) => {
    const item = e.detail && e.detail.items && e.detail.items[0];
    if (!item) {
      debug('variant:add with no items[] in the payload — ignoring');
      return;
    }
    debug('variant:add — product_id ' + item.product_id + ', variant/sku id ' + item.id);

    // Always use the tag selector: the Nosto template's wrapper is
    // <div class="product-card">, so closest('.product-card') would match that instead
    // of the theme's <product-card> element.
    const card = e.target.closest('product-card');

    let surface;
    if (card) {
      debug('path: in-card form (single variant) — resolving surface from the event target');
      surface = resolveSurface(card);
    } else if (e.target.closest('quick-buy-modal')) {
      debug('path: quick-buy modal — no card ancestor (reparented to <body>), using click-time pending');
      // INVARIANT: read `pending` synchronously, here — not inside the nostojs()
      // callback, which is queued and can run after the clear. QuickBuyModal hides
      // itself on any document-level variant:add in drawer mode, so dialog:after-hide
      // follows every successful add from the modal.
      const fresh = pending && Date.now() - pending.t < PENDING_TTL;
      if (!pending) debug('  …but pending is empty → will not record');
      else if (!fresh) debug('  …but pending is stale (>5min) → will not record; the clear is probably broken');
      surface = fresh ? pending.surface : null;
      pending = null;
    } else {
      debug('path: neither a card nor a modal (PDP main form / cart page) → not recording');
      return;
    }

    record(surface, item.product_id, item.id);
  });
}

// Covers both feature sets — it resolves recommendation surfaces (`cp`) and search
// surfaces (SERP / category / autocomplete) — so either setting arms it. With both off
// there is no Nosto surface to attribute to, and window.nostojs isn't loaded to receive
// it. See _nosto-flags.js.
if (anyEnabled()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
